import React, { useState } from 'react';
import { Shuffle, Users, AlertCircle, CheckCircle, Play } from 'lucide-react';
import { colors } from '../../constants/theme';
import { competitionService } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';

export default function GroupsSimulator() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [competitionName, setCompetitionName] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [confirmSuccess, setConfirmSuccess] = useState(null);

  const runSimulation = async () => {
    setLoading(true);
    setError(null);
    setConfirmError(null);
    setConfirmSuccess(null);
    try {
      const resp = await competitionService.simulateGroups();
      if (resp.success) {
        setResult(resp.data);
      }
    } catch (e) {
      setError(e.error?.message || 'Erro ao simular formação de grupos');
    } finally {
      setLoading(false);
    }
  };

  const hasGroups = result && Object.keys(result.groups || {}).length > 0;

  const buildGroupsPayload = () => {
    const payload = {};
    for (const [category, groups] of Object.entries(result.groups)) {
      payload[category] = {};
      for (const [groupName, players] of Object.entries(groups)) {
        payload[category][groupName] = players.map(p => p.id);
      }
    }
    return payload;
  };

  const confirmGroups = async () => {
    if (!hasGroups) return;
    const ok = window.confirm(
      'Isso vai criar a competição com EXATAMENTE estes grupos e iniciar o campeonato. ' +
        'A formação não poderá mais ser re-sorteada. Deseja continuar?'
    );
    if (!ok) return;

    setConfirming(true);
    setConfirmError(null);
    setConfirmSuccess(null);
    try {
      const resp = await competitionService.confirmSimulatedGroups({
        name: competitionName.trim() || undefined,
        groups: buildGroupsPayload(),
      });
      if (resp.success) {
        setConfirmSuccess(resp.data);
        setResult(null);
      }
    } catch (e) {
      setConfirmError(e.error?.message || 'Erro ao efetivar os grupos e iniciar a competição');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold" style={{ color: colors.primary }}>
          Simular Formação de Grupos
        </h2>
        <button
          onClick={runSimulation}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: colors.primary }}
        >
          <Shuffle className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {result ? 'Refazer simulação' : 'Simular agora'}
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-2">
        <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p>
            A simulação <strong>não cria competição</strong> nem altera o banco.
            Cada execução embaralha jogadores de novo, então o resultado muda a cada clique.
          </p>
          <p className="mt-1">
            Categorias com menos de 3 atletas aprovados não aparecem (mínimo pra participar).
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {confirmSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-2">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-800">
            <p className="font-semibold">{confirmSuccess.message}</p>
            <p className="mt-1">
              Competição <strong>{confirmSuccess.competitionName}</strong> iniciada com{' '}
              {confirmSuccess.totalPlayers} atletas. Os grupos agora estão disponíveis em
              "Editar grupos" e as partidas em "Iniciar Partidas".
            </p>
          </div>
        </div>
      )}

      {loading && <LoadingSpinner size="medium" text="Simulando..." />}

      {result && !loading && (
        <div className="space-y-4">
          {Object.keys(result.groups).length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">
                Nenhuma categoria tem atletas suficientes (mínimo 3) para formar grupos.
              </p>
            </div>
          ) : (
            Object.entries(result.groups).map(([category, groups]) => {
              const summary = result.summary?.[category];
              return (
                <div key={category} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="p-4" style={{ backgroundColor: colors.primary }}>
                    <h3 className="text-white font-semibold capitalize">
                      Categoria {category}
                    </h3>
                    {summary && (
                      <p className="text-white text-opacity-80 text-sm">
                        {summary.totalPlayers} atletas · {summary.numGroups} grupo
                        {summary.numGroups > 1 ? 's' : ''} ({summary.groupSizes.join(', ')})
                      </p>
                    )}
                  </div>
                  <div className="p-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {Object.entries(groups).map(([groupName, players]) => (
                      <div key={groupName} className="border rounded-lg p-3">
                        <h4 className="font-medium mb-2 capitalize" style={{ color: colors.primary }}>
                          {groupName.replace('grupo', 'Grupo ')}
                        </h4>
                        <ul className="space-y-1">
                          {players.map((p, idx) => (
                            <li key={p.id} className="flex items-center gap-2 text-sm">
                              <span className="text-gray-400 w-5">{idx + 1}.</span>
                              <span>{p.name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}

          {hasGroups && (
            <div className="bg-white rounded-lg shadow p-4 space-y-3 border-t-4" style={{ borderColor: colors.primary }}>
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p>
                  Gostou desta formação? Você pode <strong>efetivá-la</strong> para criar a
                  competição com exatamente estes grupos e iniciar o campeonato.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da competição (opcional)
                  </label>
                  <input
                    type="text"
                    value={competitionName}
                    onChange={e => setCompetitionName(e.target.value)}
                    placeholder="Ex: Copa Goleiro Raiz 2026"
                    disabled={confirming}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 disabled:opacity-50"
                    style={{ '--tw-ring-color': colors.primary }}
                  />
                </div>
                <button
                  onClick={confirmGroups}
                  disabled={confirming}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: colors.primary }}
                >
                  <Play className={`h-4 w-4 ${confirming ? 'animate-pulse' : ''}`} />
                  {confirming ? 'Iniciando...' : 'Efetivar grupos e iniciar campeonato'}
                </button>
              </div>

              {confirmError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {confirmError}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
